import { customAlphabet } from 'nanoid'
import { z } from 'zod'
import { IFRAME_CLASS, IFRAME_ID, WIDGET_TYPES } from '@/constants'
import { WidgetAttributesSchema } from '@/schemas/WidgetAttributesSchema'
import { queryUtmParametersFromParent } from './queryParametersUtil'
import { resolveReportablePageUrl } from './resolveReportablePageUrl'

const nanoid = customAlphabet('1234567890abcdef', 5)

function readHostPageUrl(): string | null {
  const reportableUrl = resolveReportablePageUrl()
  if (reportableUrl) return reportableUrl
  try {
    const { origin, pathname } = new URL(window.top?.location.href ?? '')
    return `${origin}${pathname}`
  } catch {
    return null
  }
}

export function createIframe(
  widgetAttributes: z.infer<typeof WidgetAttributesSchema>,
  iFrameUrl: string,
) {
  const { widgetId, widgetType, preview, widgetMode } = widgetAttributes
  const iframe = document.createElement('iframe')
  iframe.setAttribute('id', `${IFRAME_ID}-${nanoid()}`)
  iframe.setAttribute('class', IFRAME_CLASS)
  iframe.setAttribute('title', `${widgetType} widget`)

  let newIframeUrl = new URL(iFrameUrl)

  // Here we are filtering UTM parameters from the parent URL
  const utmParams: URLSearchParams = queryUtmParametersFromParent()

  const previewSegment = preview === 'true' ? 'preview' : 'view'
  const widgetTypeSegment = WIDGET_TYPES[widgetType].type
  const widgetEntryPointSegment = WIDGET_TYPES[widgetType].entryPoint

  const combinedUrlString =
    `${iFrameUrl}/widgets/${widgetTypeSegment}/${previewSegment}/${widgetId}/${widgetEntryPointSegment}`.toLowerCase()

  newIframeUrl = new URL(combinedUrlString)

  // Update the URLSearchParams with only the ones we want to keep (GA4 UTM)
  newIframeUrl.search = utmParams.toString()

  if (widgetType === WIDGET_TYPES.Prospect.type) {
    newIframeUrl.searchParams.set('widgetMode', widgetMode)
    const hostPageUrl = readHostPageUrl()
    if (hostPageUrl) {
      newIframeUrl.searchParams.set('hostPageUrl', hostPageUrl)
    }
  }

  iframe.setAttribute('src', newIframeUrl.toString())
  // Apple Pay/Google Pay iframe attribute
  iframe.setAttribute('allow', 'payment')

  return iframe
}
